import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LOGO_IMG } from "@/lib/brand";
import { PostcardPreview } from "@/components/PostcardPreview";
import {
  Gauge, MapPin, LinkSimple, Kanban, CurrencyDollar, SignOut,
  X, Copy, ChatText, Plus, Clock, CheckCircle, LockSimple,
} from "@phosphor-icons/react";

const NAV = [
  { id: "overview", label: "Dashboard", icon: Gauge },
  { id: "zones", label: "Zone Availability", icon: MapPin },
  { id: "proposal", label: "Proposal Builder", icon: LinkSimple },
  { id: "leads", label: "Lead Pipeline", icon: Kanban },
  { id: "earnings", label: "My Earnings", icon: CurrencyDollar },
];

const STAGES = ["New Lead", "Contacted", "Demo Sent", "Proposal Link Sent", "Closed Won", "Closed Lost"];

function fmt(n) { return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }

export default function SalesRepDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("overview");
  const [stats, setStats] = useState(null);
  const [prefill, setPrefill] = useState([]);

  const loadStats = () => api.get("/v1/sales/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 bg-navy-900 text-white flex lg:flex-col shrink-0">
        <div className="p-5 hidden lg:block"><img src={LOGO_IMG} alt="PMI" className="h-10 w-auto" /></div>
        <nav className="flex lg:flex-col overflow-x-auto flex-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setSection(n.id)} data-testid={`sales-nav-${n.id}`}
              className={`flex items-center gap-3 px-5 py-4 text-sm font-[600] whitespace-nowrap transition-colors ${section === n.id ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-navy-800"}`}>
              <n.icon size={18} weight="duotone" /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/login"); }} data-testid="sales-logout" className="m-3 hidden lg:flex items-center gap-3 px-4 py-3 text-sm text-slate-400 hover:bg-navy-800">
          <SignOut size={18} /> Sign out
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Top header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display text-xl tracking-tight text-navy-900" data-testid="rep-name">{user?.name || "Sales Rep"}</p>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="rep-zips">
              {(stats?.assignedZipCodes || []).map((z) => (
                <span key={z} aria-label={`Territory ${z}`} className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5">{z}</span>
              ))}
            </div>
          </div>
          <button onClick={() => setSection("proposal")} data-testid="header-create-proposal"
            className="flex items-center gap-2 bg-navy-900 text-white px-4 py-2.5 text-sm font-[600] hover:bg-emerald-700 transition-colors">
            <Plus size={16} weight="bold" /> Create Proposal Link
          </button>
        </header>

        <main className="p-6 max-w-6xl">
          {section === "overview" && <OverviewModule stats={stats} go={setSection} />}
          {section === "zones" && <ZonesModule onChange={loadStats} onSendToProposal={(items) => { setPrefill(items); setSection("proposal"); }} />}
          {section === "proposal" && <ProposalModule stats={stats} prefill={prefill} />}
          {section === "leads" && <LeadsModule />}
          {section === "earnings" && <EarningsModule stats={stats} />}
        </main>
      </div>
    </div>
  );
}

/* ---------- Module D-lite: Overview KPIs ---------- */
function OverviewModule({ stats, go }) {
  if (!stats) return <p className="text-slate-400">Loading…</p>;
  const c = stats.commissions || {};
  const inv = stats.inventory || {};
  const cards = [
    { label: "Gross Revenue Generated", value: fmt(stats.grossSales), color: "text-navy-900" },
    { label: "Pending Commissions", value: fmt(c.pending + c.approved), color: "text-amber-600" },
    { label: "Paid Commissions", value: fmt(c.paid), color: "text-emerald-700" },
    { label: "Inventory Remaining", value: inv.capacityFull ? `${inv.remainingFull}/${inv.capacityFull}` : "—", color: "text-emerald-700" },
    { label: "Deals Closed", value: stats.dealsClosed, color: "text-navy-900" },
    { label: "Active Leads", value: stats.activeLeads, color: "text-navy-900" },
    { label: "Active Holds", value: stats.activeHolds, color: "text-amber-600" },
  ];
  return (
    <div>
      <h1 className="font-display text-3xl tracking-tight mb-6">Welcome back</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {cards.map((k) => (
          <div key={k.label} className="bg-white p-6" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500">{k.label}</p>
            <p className={`font-display text-4xl tracking-tight mt-2 ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={() => go("zones")} className="bg-white border border-slate-200 px-5 py-3 text-sm font-[600] hover:border-navy-900 transition-colors">View Zone Availability</button>
        <button onClick={() => go("proposal")} className="bg-white border border-slate-200 px-5 py-3 text-sm font-[600] hover:border-navy-900 transition-colors">Build a Proposal</button>
      </div>
    </div>
  );
}

/* ---------- Module A: Zone & Category Availability ---------- */
function ZonesModule({ onChange, onSendToProposal }) {
  const [zones, setZones] = useState([]);
  const [inv, setInv] = useState(null);
  const [pubZones, setPubZones] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [previewZip, setPreviewZip] = useState("");
  const [cart, setCart] = useState([]); // categoryId[] within previewZip
  const [holding, setHolding] = useState(false);

  const load = () => api.get("/v1/sales/zones").then(({ data }) => { setZones(data.zones || []); setInv(data.inventory || null); }).catch(() => {});
  const loadPub = () => api.get("/zones").then(({ data }) => setPubZones(data || [])).catch(() => {});
  const loadCamps = () => api.get("/mailer-campaigns").then(({ data }) => setCampaigns(data || [])).catch(() => {});
  useEffect(() => { load(); loadPub(); loadCamps(); const t = setInterval(() => { load(); loadPub(); loadCamps(); }, 7000); return () => clearInterval(t); }, []);

  const repZips = useMemo(() => [...new Set(zones.flatMap((z) => z.slots.map((s) => s.zipCode)))].sort(), [zones]);
  useEffect(() => { if (repZips.length && !previewZip) setPreviewZip(repZips[0]); }, [repZips, previewZip]);
  const previewZone = pubZones.find((z) => z.zipCode === previewZip);

  const catSig = (previewZone?.categories || []).map((c) => `${c.categoryId}:${c.unavailable}:${c.fullAvailable}`).join("|");
  useEffect(() => {
    setCart((prev) => prev.filter((cid) => {
      const c = previewZone?.categories?.find((x) => x.categoryId === cid);
      return c && !c.unavailable && c.fullAvailable;
    }));
    // eslint-disable-next-line
  }, [previewZip, catSig]);

  const toggle = (c) => setCart((prev) => prev.includes(c.categoryId) ? prev.filter((x) => x !== c.categoryId) : [...prev, c.categoryId]);
  const selCats = (previewZone?.categories || []).filter((c) => cart.includes(c.categoryId));
  const availHalf = (c) => (c.halves || []).find((h) => h.status === "AVAILABLE");
  const total = selCats.reduce((s, c) => s + (c.fullPrice || 0), 0);

  const addToProposal = () => {
    const items = selCats
      .map((c) => ({ zipCode: previewZip, categoryId: c.categoryId, category: c.name, fullPrice: c.fullPrice, slotId: availHalf(c)?.slotId }))
      .filter((i) => i.slotId);
    if (!items.length) { toast.error("Select at least one open slot"); return; }
    onSendToProposal && onSendToProposal(items);
  };

  const holdAll = async () => {
    const ids = selCats.map((c) => availHalf(c)?.slotId).filter(Boolean);
    if (!ids.length) return;
    setHolding(true);
    try {
      for (const slotId of ids) await api.post("/v1/sales/zones/slots/hold", { slotId, full: true });
      toast.success(`Held ${ids.length} full slot(s) for 48h`);
      setCart([]); load(); loadPub(); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setHolding(false); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl tracking-tight mb-1">Postcard Zone Availability</h1>
      <p className="text-sm text-slate-500 mb-4">Tap open slots on the visual 9×12 mailer to build a pitch, then add them to a proposal or hold them 48 hours.</p>
      {inv && (
        <div data-testid="zone-inventory-summary" className="mb-6 bg-white border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Remaining Postcard Inventory</p>
            <p className="font-display text-3xl tracking-tight text-navy-900 mt-1" data-testid="inventory-remaining-full">{inv.remainingFull}<span className="text-slate-400 text-lg"> / {inv.capacityFull} full slots</span></p>
          </div>
          <div className="flex-1 min-w-[180px] max-w-xs">
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
              <div className="bg-navy-900" style={{ width: `${inv.capacityFull ? (inv.filledFull / inv.capacityFull) * 100 : 0}%` }} />
              <div className="bg-amber-500" style={{ width: `${inv.capacityFull ? (inv.filledHalf / inv.capacityFull) * 50 : 0}%` }} />
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">{inv.filledFull} full occupied · {inv.filledHalf} half-filled · {inv.occupiedHalfUnits} of {inv.capacityFull * 2} half-units used</p>
          </div>
        </div>
      )}

      {repZips.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Campaign</p>
          <select value={previewZip} onChange={(e) => { setPreviewZip(e.target.value); setCart([]); }} data-testid="rep-preview-zip"
            className="border border-slate-300 px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none">
            {repZips.map((z) => <option key={z} value={z}>Zip {z}</option>)}
          </select>
          {(() => {
            const c = campaigns.find((c) => (c.zipCodes || []).includes(previewZip));
            return c && !c.id.startsWith("zip-") ? <span className="text-sm font-[600] text-navy-900" data-testid="rep-campaign-descriptor">{c.descriptor}</span> : null;
          })()}
        </div>
      )}

      <div data-testid="rep-preview-wrap">
        {previewZone
          ? <PostcardPreview zone={previewZone} title="9×12 Mailer · Live Pitch View" downloadable interactive generic selectedIds={cart} onSelect={toggle} />
          : <div className="bg-white border border-slate-200 p-12 text-center text-slate-400">No zones in your territory yet.</div>}
      </div>

      {selCats.length > 0 && (
        <div className="sticky bottom-4 z-30 bg-white border-2 border-navy-900 shadow-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid="rep-cart-bar">
          <p className="text-sm font-[600] text-navy-900">{selCats.length} slot(s) · {fmt(total)} <span className="text-slate-400 font-normal">selected in {previewZip}</span></p>
          <div className="flex gap-2">
            <button onClick={() => setCart([])} data-testid="rep-clear-cart"
              className="border border-slate-300 text-slate-500 px-4 py-2 text-sm font-[600] hover:border-red-400 hover:text-red-600 transition-colors">Clear</button>
            <button onClick={holdAll} disabled={holding} data-testid="rep-hold-48h"
              className="border border-navy-900 text-navy-900 px-4 py-2 text-sm font-[600] hover:bg-slate-50 transition-colors disabled:opacity-50">
              <Clock size={14} className="inline mr-1" />{holding ? "Holding…" : "Hold 48h"}
            </button>
            <button onClick={addToProposal} data-testid="rep-add-proposal"
              className="bg-navy-900 text-white px-4 py-2 text-sm font-[600] hover:bg-emerald-700 transition-colors">
              <Plus size={14} className="inline mr-1" />Add to Proposal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Module B: Proposal & Checkout Link Builder ---------- */
function ProposalModule({ stats, prefill }) {
  const [zones, setZones] = useState([]);
  const [packages, setPackages] = useState([]);
  const [contact, setContact] = useState({ businessName: "", ownerName: "", email: "", phone: "" });
  const [selections, setSelections] = useState({}); // groupKey -> { slotId, kind, halfPrice, fullPrice }
  const [pkgIds, setPkgIds] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/v1/sales/zones").then(({ data }) => setZones(data.zones || [])).catch(() => {});
    api.get("/ai-packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!prefill || !prefill.length) return;
    setSelections((prev) => {
      const next = { ...prev };
      prefill.forEach((i) => { next[`${i.zipCode}|${i.category}`] = { slotId: i.slotId, kind: "FULL", halfPrice: i.fullPrice, fullPrice: i.fullPrice }; });
      return next;
    });
    toast.success(`${prefill.length} slot(s) added from the mailer`);
  }, [prefill]);

  const groups = useMemo(() => {
    const slots = zones.flatMap((z) => z.slots);
    const m = {};
    slots.forEach((s) => {
      const key = `${s.zipCode}|${s.tradeCategory}`;
      if (!m[key]) m[key] = { key, zipCode: s.zipCode, category: s.tradeCategory, order: s.categoryOrder ?? 999, halfPrice: s.halfPrice, fullPrice: s.fullPrice, halves: [] };
      m[key].halves.push(s);
    });
    return Object.values(m)
      .map((g) => { g.halves.sort((a, b) => (a.half || "").localeCompare(b.half || "")); return g; })
      .filter((g) => g.halves.some((h) => h.status === "AVAILABLE" || h.status === "HELD"))
      .sort((a, b) => a.zipCode.localeCompare(b.zipCode) || a.order - b.order);
  }, [zones]);

  const bothAvail = (g) => g.halves.length === 2 && g.halves.every((h) => h.status === "AVAILABLE" || h.status === "HELD");
  const cs = stats?.commissionStructure || {};
  const selList = Object.values(selections);
  const postcardTotal = selList.reduce((s, x) => s + (x.kind === "FULL" ? x.fullPrice : x.halfPrice), 0);
  const chosenPkgs = packages.filter((p) => pkgIds.includes(p.id));
  const setupTotal = chosenPkgs.reduce((s, p) => s + (p.setup_price ?? p.price ?? 0), 0);
  const monthlyTotal = chosenPkgs.reduce((s, p) => s + (p.monthly_price ?? 0), 0);
  const commission =
    postcardTotal * (cs.postcardsOneTimePct || 0) / 100 +
    setupTotal * (cs.aiSetupOneTimePct || 0) / 100 +
    monthlyTotal * (cs.aiMonthlyRecurringPct || 0) / 100;

  const setKind = (g, kind) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (kind === "SKIP") { delete next[g.key]; return next; }
      const pick = g.halves.find((h) => h.status === "AVAILABLE") || g.halves.find((h) => h.status === "HELD") || g.halves[0];
      next[g.key] = { slotId: pick.slotId, kind, halfPrice: g.halfPrice, fullPrice: g.fullPrice };
      return next;
    });
  };

  const generate = async () => {
    if (!contact.email || !contact.businessName) { toast.error("Business name and email are required"); return; }
    if (!selList.length && !pkgIds.length) { toast.error("Select at least one slot or service"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/v1/sales/proposals/checkout-link", {
        slots: selList.map((x) => ({ slotId: x.slotId, kind: x.kind })),
        packageIds: pkgIds, businessName: contact.businessName, clientEmail: contact.email,
        origin_url: window.location.origin,
      });
      setResult(data);
      toast.success("Attributed checkout link generated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight mb-1">Proposal Builder</h1>
          <p className="text-sm text-slate-500">Generate an attributed checkout link in seconds.</p>
        </div>
        <div className="bg-white border border-slate-200 p-6 space-y-3" data-testid="proposal-contact">
          <h3 className="font-display text-lg tracking-tight">Prospect Contact</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input placeholder="Business name" value={contact.businessName} onChange={(e) => setContact({ ...contact, businessName: e.target.value })} data-testid="proposal-business" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <input placeholder="Owner name" value={contact.ownerName} onChange={(e) => setContact({ ...contact, ownerName: e.target.value })} className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <input placeholder="Email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="proposal-email" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-display text-lg tracking-tight mb-3">Postcard Slots <span className="text-slate-400 text-sm">(half or full per category)</span></h3>
          {groups.length === 0 ? <p className="text-sm text-slate-400">No available or held slots. Reserve one in Zone Availability.</p> : (
            <div className="space-y-2">
              {groups.map((g) => {
                const sel = selections[g.key];
                return (
                  <div key={g.key} data-testid={`proposal-group-${g.zipCode}-${g.category}`}
                    className={`flex items-center justify-between gap-3 border p-3 text-sm ${sel ? "border-navy-900 bg-slate-50" : "border-slate-200"}`}>
                    <div>
                      <p className="font-[600] text-navy-900">{g.zipCode} · {g.category}</p>
                      <p className="text-[11px] text-slate-400">Half {fmt(g.halfPrice)} · Full {fmt(g.fullPrice)}</p>
                    </div>
                    <select value={sel?.kind || "SKIP"} onChange={(e) => setKind(g, e.target.value)} data-testid={`proposal-kind-${g.zipCode}-${g.category}`}
                      className="border border-slate-300 px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none">
                      <option value="SKIP">Skip</option>
                      <option value="HALF">Half · {fmt(g.halfPrice)}</option>
                      <option value="FULL" disabled={!bothAvail(g)}>Full · {fmt(g.fullPrice)}</option>
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-display text-lg tracking-tight mb-3">AI Services</h3>
          <div className="space-y-2">
            {packages.map((p) => (
              <label key={p.id} className={`flex items-center justify-between gap-2 border p-3 cursor-pointer text-sm ${pkgIds.includes(p.id) ? "border-navy-900 bg-slate-50" : "border-slate-200"}`}>
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={pkgIds.includes(p.id)} onChange={() => toggle(pkgIds, setPkgIds, p.id)} data-testid={`proposal-pkg-${p.id}`} />
                  {p.name}
                </span>
                <span className="text-slate-500">{fmt(p.setup_price ?? p.price)} + {fmt(p.monthly_price)}/mo</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="lg:col-span-1">
        <div className="bg-white border border-slate-200 p-6 sticky top-6" data-testid="proposal-summary">
          <h3 className="font-display text-xl tracking-tight mb-4">Deal Summary</h3>
          <div className="space-y-2 text-sm">
            <Row label="Postcard total" value={fmt(postcardTotal)} />
            <Row label="AI setup total" value={fmt(setupTotal)} />
            <Row label="Monthly recurring" value={`${fmt(monthlyTotal)}/mo`} />
            <div className="border-t border-dashed border-slate-300 pt-2 mt-2">
              <Row label="Your commission" value={fmt(commission)} strong emerald testid="proposal-commission" />
            </div>
          </div>
          {!result ? (
            <button onClick={generate} disabled={loading} data-testid="generate-link-btn"
              className="w-full mt-5 bg-navy-900 text-white py-3.5 font-[600] hover:bg-emerald-700 transition-colors disabled:opacity-60">
              {loading ? "Generating…" : "Generate & Send Checkout Link"}
            </button>
          ) : (
            <div className="mt-5 text-center" data-testid="proposal-result">
              <div className="flex justify-center bg-slate-50 border border-slate-200 p-4">
                <QRCodeSVG value={result.checkout_url} size={140} />
              </div>
              <p className="text-[11px] text-slate-400 mt-2 break-all">Ref {result.referralCode}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { navigator.clipboard?.writeText(result.checkout_url); toast.success("Link copied"); }} data-testid="copy-link-btn"
                  className="flex-1 flex items-center justify-center gap-1 border border-slate-300 py-2.5 text-sm font-[600] hover:border-navy-900"><Copy size={15} /> Copy</button>
                <a href={`sms:${contact.phone}?body=${encodeURIComponent("Your PMI proposal: " + result.checkout_url)}`} data-testid="send-sms-btn"
                  className="flex-1 flex items-center justify-center gap-1 bg-emerald-600 text-white py-2.5 text-sm font-[600] hover:bg-emerald-700"><ChatText size={15} /> SMS</a>
              </div>
              <button onClick={() => { setResult(null); setSelections({}); setPkgIds([]); setContact({ businessName: "", ownerName: "", email: "", phone: "" }); }} className="mt-3 text-xs text-slate-400 hover:text-navy-900">Build another</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong, emerald, testid }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span data-testid={testid} className={`${strong ? "font-display text-xl" : "font-[600]"} ${emerald ? "text-emerald-700" : "text-navy-900"}`}>{value}</span>
    </div>
  );
}

/* ---------- Module C: Lead Pipeline ---------- */
function LeadsModule() {
  const [leads, setLeads] = useState([]);
  const [stage, setStage] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/v1/sales/leads").then(({ data }) => setLeads(data)).catch(() => setLeads([]));
  useEffect(() => { load(); }, []);

  const update = async (lead, patch) => {
    try {
      const { data } = await api.patch(`/v1/sales/leads/${lead.id}`, patch);
      toast.success("Lead updated"); setNote("");
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      if (detail && detail.id === data.id) setDetail(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = stage ? leads.filter((l) => (l.status || "New Lead") === stage) : leads;

  return (
    <div>
      <h1 className="font-display text-3xl tracking-tight mb-1">Lead Pipeline</h1>
      <p className="text-sm text-slate-500 mb-6">Leads from your territory and referral code only.</p>
      <div className="flex gap-2 flex-wrap mb-4">
        <button onClick={() => setStage("")} className={`px-3 py-1.5 text-xs font-[600] border ${!stage ? "bg-navy-900 text-white border-navy-900" : "border-slate-300 text-slate-600"}`}>All</button>
        {STAGES.map((s) => (
          <button key={s} onClick={() => setStage(s)} data-testid={`stage-${s.replace(/ /g, "-")}`}
            className={`px-3 py-1.5 text-xs font-[600] border ${stage === s ? "bg-navy-900 text-white border-navy-900" : "border-slate-300 text-slate-600"}`}>{s}</button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="leads-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Stage</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">No leads yet.</td></tr> :
              filtered.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="px-5 py-4 font-[600]">{l.name || l.business_name || "—"}</td>
                  <td className="px-5 py-4 text-slate-500">{l.email || l.phone || "—"}</td>
                  <td className="px-5 py-4">
                    <select value={l.status || "New Lead"} onChange={(e) => update(l, { status: e.target.value })} data-testid={`lead-stage-${l.id}`}
                      className="border border-slate-300 px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none">
                      {STAGES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => setDetail(l)} data-testid={`lead-open-${l.id}`} className="text-xs font-[600] text-navy-900 hover:underline">Open</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-navy-900/60 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-detail">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl tracking-tight">{detail.name || detail.business_name || "Lead"}</h3>
              <button onClick={() => setDetail(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <p className="text-sm text-slate-500">{detail.email} {detail.phone ? `· ${detail.phone}` : ""}</p>
            <p className="text-xs text-slate-400 mt-1">Stage: {detail.status || "New Lead"}</p>
            <div className="mt-4">
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Interaction Log</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(detail.notes || []).length === 0 ? <p className="text-sm text-slate-400">No notes yet.</p> :
                  (detail.notes || []).map((n, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-100 p-2 text-sm"><p>{n.note}</p><p className="text-[10px] text-slate-400 mt-0.5">{new Date(n.at).toLocaleString()}</p></div>
                  ))}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call / SMS note…" data-testid="lead-note-input"
                className="flex-1 border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
              <button onClick={() => note && update(detail, { note })} data-testid="lead-note-save" className="bg-navy-900 text-white px-4 text-sm font-[600] hover:bg-emerald-700">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Module D: Earnings & Commissions ---------- */
function EarningsModule({ stats }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/v1/sales/commissions").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  const c = stats?.commissions || {};
  const retainers = rows.filter((r) => r.dealType === "AI_AGENT_RETAINER").length;
  const kpis = [
    { label: "Gross Revenue (all-time)", value: fmt(stats?.grossSales) },
    { label: "Pending Commissions", value: fmt((c.pending || 0) + (c.approved || 0)) },
    { label: "Paid Commissions", value: fmt(c.paid) },
    { label: "Active Recurring Retainers", value: retainers },
  ];
  const typeLabel = { POSTCARD_SLOT: "Postcard Slot", AI_AGENT_SETUP: "AI Setup", AI_AGENT_RETAINER: "AI Retainer" };
  const statusBadge = (s) => s === "PAID" ? "bg-emerald-100 text-emerald-700" : s === "CANCELLED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";

  return (
    <div>
      <h1 className="font-display text-3xl tracking-tight mb-6">My Earnings & Commissions</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-6">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white p-5" data-testid={`earn-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500">{k.label}</p>
            <p className="font-display text-3xl tracking-tight mt-1 text-navy-900">{k.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="earnings-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-3">Date</th><th className="px-5 py-3">Client</th><th className="px-5 py-3">Product</th>
              <th className="px-5 py-3 text-right">Sale</th><th className="px-5 py-3 text-right">Rate</th><th className="px-5 py-3 text-right">Earned</th><th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">No commissions yet. Close a deal to start earning.</td></tr> :
              rows.map((r) => (
                <tr key={r.commissionId} className="border-t border-slate-100" data-testid={`commission-row-${r.commissionId}`}>
                  <td className="px-5 py-4 text-slate-400">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-4">{r.clientId || "—"}</td>
                  <td className="px-5 py-4">{typeLabel[r.dealType] || r.dealType}</td>
                  <td className="px-5 py-4 text-right">{fmt(r.saleAmount)}</td>
                  <td className="px-5 py-4 text-right">{r.commissionRate}%</td>
                  <td className="px-5 py-4 text-right font-[600] text-emerald-700">{fmt(r.amount)}</td>
                  <td className="px-5 py-4"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${statusBadge(r.payoutStatus)}`}>{r.payoutStatus}</span></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
